/**
 * CalendlyInlineWidget — embedded scheduling for hospital pilot bookings.
 *
 * Reads `REACT_APP_CALENDLY_URL` from env. When set, mounts the official
 * Calendly inline embed iframe. When unset (preview/dev posture), renders
 * a polite "Schedule later" pill with a fallback to the existing lead-
 * capture form so the UI never has a dead hole.
 *
 * No API key needed for the basic inline embed — Calendly serves the
 * scheduler directly. Booking events fire through Calendly's webhooks
 * (configured separately in the Calendly dashboard) so this widget is
 * purely a frontend embed.
 *
 * Rule Zero: additive. The existing InstitutionInquiryModal lead form
 * keeps working when the env var is unset.
 */
import React, { useEffect, useRef } from 'react';
import { CalendarClock } from 'lucide-react';

const CALENDLY_URL = process.env.REACT_APP_CALENDLY_URL || '';

export function CalendlyInlineWidget({
  prefill = {},
  utm = {},
  height = 720,
  className = '',
}) {
  const ref = useRef(null);

  useEffect(() => {
    if (!CALENDLY_URL || !ref.current) return;

    // Inject the Calendly widget script once.
    const SCRIPT_ID = 'calendly-widget-script';
    const existing = document.getElementById(SCRIPT_ID);
    if (!existing) {
      const s = document.createElement('script');
      s.id = SCRIPT_ID;
      s.src = 'https://assets.calendly.com/assets/external/widget.js';
      s.async = true;
      document.head.appendChild(s);
    }
    // Inject the stylesheet once.
    const CSS_ID = 'calendly-widget-css';
    if (!document.getElementById(CSS_ID)) {
      const link = document.createElement('link');
      link.id = CSS_ID;
      link.rel = 'stylesheet';
      link.href = 'https://assets.calendly.com/assets/external/widget.css';
      document.head.appendChild(link);
    }

    // Compose URL with prefill + utm params
    let url = CALENDLY_URL;
    const params = new URLSearchParams();
    if (prefill.name) params.set('name', prefill.name);
    if (prefill.email) params.set('email', prefill.email);
    Object.entries(utm).forEach(([k, v]) => {
      if (v) params.set(`utm_${k}`, v);
    });
    if (Array.from(params).length) {
      url = `${url}${url.includes('?') ? '&' : '?'}${params.toString()}`;
    }

    // Calendly auto-binds to elements with the .calendly-inline-widget
    // class; set the URL attribute then trigger a re-init.
    ref.current.setAttribute('data-url', url);
  }, [prefill.name, prefill.email, utm.source, utm.medium, utm.campaign]);

  // Graceful no-op when not configured for this environment.
  if (!CALENDLY_URL) {
    return (
      <div
        className={`rounded-2xl border border-cn-border bg-cn-bone-100 dark:bg-cn-ink-800 p-6 text-center ${className}`}
        data-testid="calendly-fallback"
      >
        <CalendarClock className="w-6 h-6 text-cn-text-muted mx-auto mb-3" />
        <p className="text-sm text-cn-text-muted leading-relaxed">
          Direct booking is coming online soon — leave your details below
          and our team will reach out within one business day to schedule
          a discovery call.
        </p>
      </div>
    );
  }

  return (
    <div
      ref={ref}
      className={`calendly-inline-widget rounded-2xl overflow-hidden border border-cn-border bg-white ${className}`}
      style={{ minWidth: 320, height }}
      data-testid="calendly-inline-widget"
      data-url={CALENDLY_URL}
    />
  );
}

export default CalendlyInlineWidget;
